<script setup>
// 1. State for the currently active tab
const activeCategoryId = ref(null);
const route = useRoute();
const config = useRuntimeConfig();

// 2. Optimized Fetch
// Use the `query` option for cleaner URL construction
const { data, status, error } = await useLazyFetch('/category', {
  baseURL: config.public.apiBase,
  query: {
    'filter[shop_id]': route.params.id
  }
});

const categories = computed(() => data.value?.data || []);

// 3. Auto-select the first tab when data loads
watch(categories, (newCategories) => {
  if (newCategories?.length > 0 && !activeCategoryId.value) {
    activeCategoryId.value = newCategories[0].id;
  }
}, { immediate: true });

// 4. Handle Click & Scroll
const setActive = (id) => {
  activeCategoryId.value = id;
  
  // We use a specific ID prefix to avoid collisions
  const element = document.getElementById(`category-section-${id}`);
  if (element) {
    // block: 'nearest' or 'start' usually provides a better experience than default
    element.scrollIntoView({ behavior: 'smooth', block: 'start' }); 
  }
};
</script>

<template>
  <div v-if="status === 'pending'">
    <NavLoading />
  </div>

  <div v-else-if="error">
    <div class="text-error p-4">Failed to load: {{ error.message }}</div>
  </div>

  <div v-else class="flex flex-col gap-2">
    <div class="sticky top-0 z-20 w-full overflow-x-auto no-scrollbar shadow-md bg-base-100">
      <div role="tablist" class="tabs tabs-lg tabs-border tabs-boxed w-max">
        <a 
          v-for="category in categories" 
          :key="category.id" 
          role="tab" 
          class="tab transition-all duration-200"
          :class="{ 'tab-active !text-primary !border-primary': activeCategoryId === category.id }" 
          @click="setActive(category.id)"
        >
          {{ category.name }}
        </a>
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <div 
        v-for="category in categories" 
        :id="`category-section-${category.id}`" 
        :key="category.id"
      >
        <ShopTabContent 
          :id="category.id" 
          :name="category.name"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Utility classes */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>